import { deriveActualTimes, deriveDistanceTravelled, deriveTimeMarked } from '~appointments/lib/derive';
import { showAppointmentDateOrLess, showTimeMarked } from '~appointments/lib/show';
import { validateRepeatDates, validateRepeatFrequency } from '~appointments/lib/validate';

import { FieldProps } from '~components/form-fields';
import {
  FormInputActualTimes,
  FormInputDate,
  FormInputDistanceTravelled,
  FormInputRepeatDates,
  FormInputRepeatFrequency,
  FormInputSignature,
  FormInputTimeMarked,
  FormInputTravelTime,
  FormInputVehicleType,
} from '~appointments/components';

import { commonFields } from './common';

const editFields: FieldProps = {
  ...commonFields,
  client_package_name: {
    type: 'readonly',
    caption: 'Default Package',
    field: 'client.default_package.name',
    className: 'text-left',
    order: 220,
  },
};

const singleSpecific: FieldProps = {
  date: {
    type: 'custom',
    component: FormInputDate,
    caption: 'Appointment Date',
    required: true,
    colSize: 4,
    order: 120,
  },
  time_marked: {
    type: 'custom',
    caption: 'Time Marked',
    labelCaption: 'Auto marked by the system',
    colSize: 6,
    component: FormInputTimeMarked,
    order: 1110,
    deriveValue: deriveTimeMarked,
    show: showTimeMarked,
  },

  distance_travelled: {
    type: 'custom',
    caption: 'Distance Travelled',
    labelCaption: 'Entered by the care worker',
    colSize: 6,
    component: FormInputDistanceTravelled,
    deriveValue: deriveDistanceTravelled,
    show: showAppointmentDateOrLess,
    order: 1120,
  },
  travel_1: {
    type: 'custom',
    caption: 'Time Travelled (TO Client)',
    labelCaption: 'Entered by the care worker',
    component: FormInputTravelTime,
    colSize: 6,
    order: 1130,
    show: showAppointmentDateOrLess,
  },
  vehicle_type: {
    type: 'custom',
    caption: 'Vehicle Type',
    labelCaption: 'Entered by the care worker',
    component: FormInputVehicleType,
    colSize: 6,
    order: 1140,
    show: showAppointmentDateOrLess,
    options: [
      { id: 'user', name: 'My Car (Care Worker)' },
      { id: 'company', name: 'Company' },
    ],
  },
  actual_times: {
    type: 'custom',
    caption: 'Actual Times Entered',
    labelCaption: 'Entered by the care worker',
    component: FormInputActualTimes,
    deriveValue: deriveActualTimes,
    colSize: 6,
    order: 1150,
    show: showAppointmentDateOrLess,
  },
  signature: {
    type: 'custom',
    caption: 'Client Signature',
    component: FormInputSignature,
    order: 1160,
  },
};

const repeatSpecific: FieldProps = {
  repeat_dates: {
    type: 'custom',
    component: FormInputRepeatDates,
    dependencies: ['repeat', 'repeat_start', 'repeat_end'],
    validateOnSave: validateRepeatDates,
    caption: 'Repeat Dates',
    labelCaption: 'Best to select Monday as the start date, in case of future day changes',
    colSize: 4,
    order: 120,
  },
  repeat_frequency: {
    type: 'custom',
    component: FormInputRepeatFrequency,
    dependencies: ['repeat', 'repeat_weekday', 'repeat_week_count', 'skip_on_public_holidays'],
    validateOnSave: validateRepeatFrequency,
    caption: 'Frequency',
    labelCaption: "You can only select 1 week day to repeat. Once added, you can select 'Create similiar repeat'",
    colSize: 4,
    order: 130,
  },
};

const appointmentEditFields: FieldProps = {
  ...editFields,
  ...singleSpecific,
};

const repeatAppointmentEditFields: FieldProps = {
  ...editFields,
  ...repeatSpecific,
};

// TAV-2947: updating an existing Client on a Child Appointment (single or repeat Group
// Appointment) restricts Date/Repeat Settings to read-only and hides Qualifications and
// Care Worker Selection, matching the manual "Add Client" restrictions in forms/appointment/new.
// user_leave_availability/user_appointments/user_qualification_check/user_rostering_notes/
// user_availability are gated by showUserAvailability, which reads touched.user_id/date —
// Formik's submitForm marks every field touched on submit regardless of user interaction,
// so these would still surface care-worker-availability info for the SYSTEM_2022_GROUP_SESSION_USER_ID
// placeholder unless hidden alongside user_id.
const clientGroupChildEditFields: FieldProps = {
  ...appointmentEditFields,
  date: {
    ...singleSpecific.date,
    disabled: true,
  },
  qualification_level: {
    type: 'hidden',
  },
  user_id: {
    type: 'hidden',
  },
  user_leave_availability: {
    type: 'hidden',
  },
  user_appointments: {
    type: 'hidden',
  },
  user_qualification_check: {
    type: 'hidden',
  },
  user_rostering_notes: {
    type: 'hidden',
  },
  user_availability: {
    type: 'hidden',
  },
};

const repeatClientGroupChildEditFields: FieldProps = {
  ...repeatAppointmentEditFields,
  repeat_dates: {
    ...repeatSpecific.repeat_dates,
    disabled: true,
  },
  repeat_frequency: {
    ...repeatSpecific.repeat_frequency,
    disabled: true,
  },
  qualification_level: {
    type: 'hidden',
  },
  user_id: {
    type: 'hidden',
  },
  user_leave_availability: {
    type: 'hidden',
  },
  user_appointments: {
    type: 'hidden',
  },
  user_qualification_check: {
    type: 'hidden',
  },
  user_rostering_notes: {
    type: 'hidden',
  },
  user_availability: {
    type: 'hidden',
  },
};

export {
  appointmentEditFields,
  repeatAppointmentEditFields,
  clientGroupChildEditFields,
  repeatClientGroupChildEditFields,
};
